import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Paper, Typography, Button, CircularProgress } from '@mui/material';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import WifiOffRoundedIcon from '@mui/icons-material/WifiOffRounded';
import { colors, layout } from '../design-system/tokens';

/**
 * 세션 로딩/거부/오류 전체 화면.
 * - loading: 세션 조회 중
 * - denied:  로그인은 됐지만 점주 콘솔 대상 계정이 아님 (backend 403)
 * - error:   세션 조회 실패 (네트워크 등) — 재시도 제공
 */
const ConsoleStatusScreen = ({ variant, message, onRetry }) => {
  const navigate = useNavigate();

  if (variant === 'loading') {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: colors.background,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          wordBreak: 'keep-all',
          overflowWrap: 'anywhere',
        }}
      >
        <CircularProgress size={32} />
        <Typography variant="body2" sx={{ color: colors.text.muted }}>
          세션을 불러오는 중이에요…
        </Typography>
      </Box>
    );
  }

  const isDenied = variant === 'denied';
  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: colors.background,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3,
        wordBreak: 'keep-all',
        overflowWrap: 'anywhere',
      }}
    >
      <Paper
        sx={{
          p: layout.cardPadding + 1.5,
          maxWidth: 480,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
          textAlign: 'center',
        }}
      >
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: isDenied ? colors.status.danger.bg : colors.status.warning.bg,
            color: isDenied ? colors.status.danger.fg : colors.status.warning.fg,
          }}
        >
          {isDenied ? <LockRoundedIcon /> : <WifiOffRoundedIcon />}
        </Box>
        <Typography variant="h6">
          {isDenied ? '점주 콘솔을 사용할 수 없어요' : '세션을 불러오지 못했어요'}
        </Typography>
        <Typography variant="body2" sx={{ color: colors.text.muted }}>
          {message ||
            (isDenied
              ? '이 계정에는 점주 콘솔 접근 권한이 없어요. 매장 계정으로 로그인해 주세요.'
              : '네트워크 상태를 확인한 뒤 다시 시도해 주세요.')}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: layout.cardGap, mt: 1 }}>
          {!isDenied && onRetry && (
            <Button variant="contained" onClick={onRetry}>
              다시 시도
            </Button>
          )}
          <Button variant={isDenied ? 'contained' : 'outlined'} onClick={() => navigate('/')}>
            관리자 콘솔로 이동
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default ConsoleStatusScreen;
